import React, { useState } from 'react';
import styled from 'styled-components';
import { Package, Truck, CheckCircle, Clock, MapPin, Phone, Search, AlertCircle, Calendar } from 'lucide-react';
import ContentLayout from '../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const SearchSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  margin-bottom: 30px;
`;

const SearchTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchForm = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchButton = styled.button`
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
  
  &:hover {
    background: #2563eb;
  }
`;

const QuickSearch = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const QuickButton = styled.button`
  padding: 6px 12px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const TrackingResult = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  overflow: hidden;
  margin-bottom: 20px;
`;

const ResultHeader = styled.div`
  background: #f9fafb;
  padding: 25px;
  border-bottom: 1px solid #e5e7eb;
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 15px;
`;

const InfoItem = styled.div``;

const InfoLabel = styled.p`
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0 0 5px 0;
`;

const InfoValue = styled.p`
  font-size: 16px;
  color: #1f2937;
  font-weight: 600;
  margin: 0;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${props => {
    switch(props.status) {
      case 'preparing': return '#fef3c7';
      case 'shipped': return '#dbeafe';
      case 'in-transit': return '#e0f2fe';
      case 'delivered': return '#dcfce7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'preparing': return '#d97706';
      case 'shipped': return '#2563eb';
      case 'in-transit': return '#0369a1';
      case 'delivered': return '#16a34a';
      default: return '#6b7280';
    }
  }};
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
`;

const DeliveryInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  padding: 25px;
  border-bottom: 1px solid #e5e7eb;
`;

const DeliveryCard = styled.div`
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardContent = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
`;

const Timeline = styled.div`
  padding: 25px;
`;

const TimelineTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 20px 0;
`;

const TimelineList = styled.div`
  position: relative;
  padding-left: 30px;
  
  &::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 25px;
  
  &:last-child {
    padding-bottom: 0;
  }
`;

const TimelineIcon = styled.div`
  position: absolute;
  left: -23px;
  top: 0;
  width: 30px;
  height: 30px;
  background: ${props => props.active ? '#3b82f6' : '#e5e7eb'};
  border: 3px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.active ? 'white' : '#9ca3af'};
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const TimelineContent = styled.div`
  margin-left: 15px;
`;

const TimelineTime = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 5px 0;
`;

const TimelineStatus = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 5px 0;
`;

const TimelineDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
`;

const EmptyState = styled.div`
  background: white;
  padding: 60px 20px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #9ca3af;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
`;

const EmptyDescription = styled.p`
  color: #6b7280;
  margin: 0;
`;

const ErrorState = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #dc2626;
`;

function DeliveryTracking() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 더미 배송 데이터
  const dummyTrackingData = {
    'TK123456789': {
      orderNumber: 'ORD-2024-001',
      trackingNumber: 'TK123456789',
      status: 'in-transit',
      statusText: '배송중',
      estimatedDelivery: '2024-02-16',
      courier: 'CJ대한통운',
      courierPhone: '1588-1255',
      sender: 'AntsMarket',
      recipient: {
        name: '김철수',
        address: '서울시 강남구 테헤란로 123, 10층',
        phone: '010-1234-5678'
      },
      timeline: [
        {
          time: '2024-02-15 14:30',
          status: '배송 출발',
          description: '서울 허브에서 배송을 시작했습니다.',
          location: '서울허브',
          active: true
        },
        {
          time: '2024-02-15 09:15',
          status: '허브 도착',
          description: '상품이 서울 허브에 도착했습니다.',
          location: '서울허브',
          active: true
        },
        {
          time: '2024-02-14 18:20',
          status: '상품 발송',
          description: '판매자가 상품을 발송했습니다.',
          location: '경기 광명시',
          active: true
        },
        {
          time: '2024-02-14 10:00',
          status: '상품 준비',
          description: '주문하신 상품을 준비중입니다.',
          location: '판매처',
          active: true
        }
      ]
    },
    'TK987654321': {
      orderNumber: 'ORD-2024-002',
      trackingNumber: 'TK987654321',
      status: 'delivered',
      statusText: '배송완료',
      estimatedDelivery: '2024-02-10',
      courier: '로젠택배',
      courierPhone: '1588-9988',
      sender: 'AntsMarket',
      recipient: {
        name: '이영희',
        address: '서울시 서초구 강남대로 456, 5층',
        phone: '010-9876-5432'
      },
      timeline: [
        {
          time: '2024-02-10 15:42',
          status: '배송완료',
          description: '배송이 완료되었습니다. 고객님께서 직접 수령하셨습니다.',
          location: '서울시 서초구',
          active: true
        },
        {
          time: '2024-02-10 09:30',
          status: '배송중',
          description: '고객님 댁으로 배송 중입니다.',
          location: '서울시 서초구',
          active: true
        },
        {
          time: '2024-02-09 22:15',
          status: '허브 출발',
          description: '서울 허브에서 배송지로 출발했습니다.',
          location: '서울허브',
          active: true
        },
        {
          time: '2024-02-09 14:00',
          status: '상품 발송',
          description: '판매자가 상품을 발송했습니다.',
          location: '경기 성남시',
          active: true
        }
      ]
    }
  };

  const sampleTrackingNumbers = ['TK123456789', 'TK987654321'];

  const handleSearch = () => {
    if (!trackingNumber.trim()) {
      setError('운송장 번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');

    // 실제로는 API 호출
    setTimeout(() => {
      const result = dummyTrackingData[trackingNumber];
      if (result) {
        setTrackingResult(result);
      } else {
        setError('해당 운송장 번호로 조회된 결과가 없습니다. 운송장 번호를 다시 확인해주세요.');
        setTrackingResult(null);
      }
      setIsLoading(false);
    }, 1000);
  };

  const handleQuickSearch = (number) => {
    setTrackingNumber(number);
    setTrackingResult(dummyTrackingData[number]);
    setError('');
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'preparing': return <Clock size={16} />;
      case 'shipped': return <Package size={16} />;
      case 'in-transit': return <Truck size={16} />;
      case 'delivered': return <CheckCircle size={16} />;
      default: return <Package size={16} />;
    }
  };

  const getTimelineIcon = (index, isActive) => {
    if (index === 0 && trackingResult?.status === 'delivered') {
      return <CheckCircle size={14} />;
    }
    return <Package size={14} />;
  };

  return (
    <ContentLayout 
      title="배송 추적"
      subtitle="운송장 번호를 입력하시면 실시간으로 배송 현황을 확인하실 수 있습니다"
      centerTitle={false}
    >
      <Container>

      <SearchSection>
        <SearchTitle>
          <Search size={20} />
          운송장 조회
        </SearchTitle>
        
        <SearchForm>
          <SearchInput
            type="text"
            placeholder="운송장 번호를 입력하세요 (예: TK123456789)"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <SearchButton onClick={handleSearch} disabled={isLoading}>
            {isLoading ? <Clock size={16} /> : <Search size={16} />}
            {isLoading ? '조회중...' : '조회하기'}
          </SearchButton>
        </SearchForm>

        <div style={{marginBottom: '10px', fontSize: '12px', color: '#6b7280'}}>
          빠른 조회 (샘플):
        </div>
        <QuickSearch>
          {sampleTrackingNumbers.map(number => (
            <QuickButton key={number} onClick={() => handleQuickSearch(number)}>
              {number}
            </QuickButton>
          ))}
        </QuickSearch>
      </SearchSection>

      {error && (
        <ErrorState>
          <AlertCircle size={20} />
          {error}
        </ErrorState>
      )}

      {trackingResult && (
        <TrackingResult>
          <ResultHeader>
            <OrderInfo>
              <InfoItem>
                <InfoLabel>주문번호</InfoLabel>
                <InfoValue>{trackingResult.orderNumber}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>운송장번호</InfoLabel>
                <InfoValue>{trackingResult.trackingNumber}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>택배회사</InfoLabel>
                <InfoValue>{trackingResult.courier}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>예상 배송일</InfoLabel>
                <InfoValue>{trackingResult.estimatedDelivery}</InfoValue>
              </InfoItem>
            </OrderInfo>
            
            <StatusBadge status={trackingResult.status}>
              {getStatusIcon(trackingResult.status)}
              {trackingResult.statusText}
            </StatusBadge>
          </ResultHeader>

          <DeliveryInfo>
            <DeliveryCard>
              <CardTitle>
                <MapPin size={16} />
                수령인 정보
              </CardTitle>
              <CardContent>
                <div><strong>{trackingResult.recipient.name}</strong></div>
                <div>{trackingResult.recipient.address}</div>
                <div>{trackingResult.recipient.phone}</div>
              </CardContent>
            </DeliveryCard>

            <DeliveryCard>
              <CardTitle>
                <Phone size={16} />
                택배회사 연락처
              </CardTitle>
              <CardContent>
                <div><strong>{trackingResult.courier}</strong></div>
                <div>고객센터: {trackingResult.courierPhone}</div>
                <div>운영시간: 평일 09:00-18:00</div>
              </CardContent>
            </DeliveryCard>
          </DeliveryInfo>

          <Timeline>
            <TimelineTitle>배송 현황</TimelineTitle>
            <TimelineList>
              {trackingResult.timeline.map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineIcon active={item.active}>
                    {getTimelineIcon(index, item.active)}
                  </TimelineIcon>
                  <TimelineContent>
                    <TimelineTime>{item.time}</TimelineTime>
                    <TimelineStatus>{item.status}</TimelineStatus>
                    <TimelineDescription>
                      {item.description}
                      {item.location && ` (${item.location})`}
                    </TimelineDescription>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </TimelineList>
          </Timeline>
        </TrackingResult>
      )}

      {!trackingResult && !error && !isLoading && (
        <EmptyState>
          <EmptyIcon>
            <Package size={40} />
          </EmptyIcon>
          <EmptyTitle>배송 추적을 시작해보세요</EmptyTitle>
          <EmptyDescription>
            위에 운송장 번호를 입력하시거나, 샘플 번호를 클릭해서 배송 현황을 확인해보세요
          </EmptyDescription>
        </EmptyState>
      )}
      </Container>
    </ContentLayout>
  );
}

export default DeliveryTracking;